"use client";

import { useRef, useState } from "react";

const digits = (value: string) => value.normalize("NFKC").replace(/\D/g, "").slice(0, 6);

export function VerificationCodeInput({ value, onChange, disabled, label }: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [cursor, setCursor] = useState(0);

  function change(raw: string) {
    const next = digits(raw);
    setCursor(Math.min(next.length, 5));
    onChange(next);
  }

  // Keep one real input so keyboard navigation, SMS autofill and screen readers
  // work with one complete code, while displaying six separate visual slots.
  return <span className={`relative block ${disabled ? "opacity-60" : ""}`}>
    <input
      ref={input}
      aria-label={label}
      className="absolute inset-0 z-10 h-full w-full cursor-text opacity-0 disabled:cursor-wait"
      type="text"
      autoComplete="one-time-code"
      inputMode="numeric"
      pattern="[0-9]{6}"
      maxLength={6}
      value={value}
      disabled={disabled}
      onChange={event => change(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onSelect={event => setCursor(Math.min(event.currentTarget.selectionStart ?? value.length, 5))}
      onClick={event => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const slot = Math.max(0, Math.min(5, Math.floor((event.clientX - bounds.left) / (bounds.width / 6))));
        const start = Math.min(slot, value.length);
        event.currentTarget.setSelectionRange(start, Math.min(start + 1, value.length));
        setCursor(start);
      }}
      onPaste={event => {
        const pasted = digits(event.clipboardData.getData("text"));
        if (!pasted) return;
        event.preventDefault();
        change(pasted);
        requestAnimationFrame(() => input.current?.setSelectionRange(pasted.length, pasted.length));
      }}
    />
    <span aria-hidden="true" className="grid grid-cols-6 gap-2">
      {Array.from({ length: 6 }, (_, index) => <span key={index} className={`flex h-12 min-w-0 items-center justify-center rounded-xl border bg-[var(--color-sunken)] text-xl tabular-nums ${focused && cursor === index ? "border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]" : "border-[var(--color-line-strong)]"}`}>
        {value[index] ?? ""}
      </span>)}
    </span>
  </span>;
}
